import React, { useState, SyntheticEvent, useMemo } from 'react'
import { apiHost } from '../../config'

interface RequestField {
  label: string
  type: 'text' | 'checkbox' | 'select'
  values?: string[]
}

interface CommonData {
  clientName: string
  clientTransactionId: string
  agentId: string
  agentGroup: string
}

interface RequestData {
  common: CommonData
  [key: string]: any // Allow any other string keys
}

interface LinkConfig {
  linkType: string
  endpoint: string
  requestData: RequestData
}

const LinkContent = ({
  linkConfigs,
  unAuthorizedEnv,
  unAuthorizedEnvMessage
}: {
  linkConfigs: LinkConfig[]
  unAuthorizedEnv: boolean
  unAuthorizedEnvMessage: string
}) => {
  console.log('linkConfigs in LinkContent component:', linkConfigs)

  const [linkType, setLinkType] = useState<string>('')
  const [formData, setFormData] = useState<{ [key: string]: string | boolean | number }>({})
  const [response, setResponse] = useState<any>(null)

  const selectedLink: LinkConfig | null = useMemo(() => {
    if (!linkType) {
      return null
    }
    return linkConfigs.find((item) => item.linkType === linkType) || null
  }, [linkType, linkConfigs])

  if (unAuthorizedEnv) {
    return <h3>{unAuthorizedEnvMessage}</h3>
  }

  const handleLinkTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setLinkType(event.target.value)
    setFormData({}) // Reset form data when link type changes
    setResponse(null)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target
    const checked = (event.target as HTMLInputElement).checked
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault()
    if (!selectedLink) return

    const requestData: { [key: string]: any } = { ...selectedLink.requestData.common }

    Object.keys(selectedLink.requestData).forEach((key) => {
      if (key !== 'common') {
        requestData[key] = formData[key]
      }
    })

    const host = window.location.origin === undefined ? apiHost : window.location.origin
    fetch(host + selectedLink.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ common: selectedLink.requestData.common, ...formData })
    })
      .then((response) => response.json())
      .then((data) => {
        setResponse(data)
      })
      .catch((error) => {
        console.error('Error:', error)
        setResponse({ error: 'An error occurred while fetching the data.' })
      })
  }

  const renderFormFields = () => {
    if (!selectedLink || !selectedLink.requestData) return null

    const fields = Object.keys(selectedLink.requestData).filter((key) => key !== 'common')

    return fields.map((key) => {
      const field = selectedLink.requestData[key]
      return (
        <div className='row mb-0' key={key}>
          <div className='col-5'>
            <label className='col-form-label'>{field.label}</label>
          </div>
          <div className='col-4'>{renderInputField(key, field)}</div>
        </div>
      )
    })
  }

  const renderInputField = (key: string, field: RequestField) => {
    const value = formData[key]

    switch (field.type) {
      case 'text': {
        const displayValue = typeof value === 'string' || typeof value === 'number' ? value : ''
        return <input type='text' name={key} value={displayValue} onChange={handleInputChange} />
      }
      case 'checkbox': {
        const isChecked = typeof value === 'boolean' ? value : false
        return <input type='checkbox' name={key} checked={isChecked} onChange={handleInputChange} />
      }
      case 'select': {
        const displayValue = typeof value === 'string' || typeof value === 'number' ? value : ''
        return (
          <select name={key} value={displayValue} onChange={handleInputChange}>
            <option value=''>Make a selection...</option>
            {field.values &&
              field.values.map((val: string) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
          </select>
        )
      }
      default:
        return null
    }
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexBasis: '1 1 100%',
          flexDirection: 'column',
          gap: '2rem',
          justifyContent: 'center'
        }}
      >
        <div className='container-fluid'>
          <div className='row'>
            <div className='col text-center page-header'>
              <h3>Link API Test page(dynamic) - Generate link for given link type</h3>
            </div>
          </div>
          <div className='row mb-0'>
            <div className='col-5'>
              <label className='col-form-label'>
                Link Type<span className='required-asterisk'>*</span>
              </label>
            </div>
            <div className='col-4'>
              <select value={linkType} onChange={handleLinkTypeChange}>
                <option value=''>Select Link Type</option>
                {linkConfigs.map((item) => (
                  <option key={item.linkType} value={item.linkType}>
                    {item.linkType}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {renderFormFields()}

          {selectedLink && (
            <div className='row mb-0'>
              <div className='col-5'></div>
              <div className='col-4'>
                <input
                  style={{ marginTop: '20px', background: '#285A93', color: '#fff' }}
                  type='button'
                  id='submit'
                  value='Submit'
                  onClick={handleSubmit}
                  aria-label='submit'
                  data-automation-id='submitbtn'
                />
              </div>
            </div>
          )}

          {response && (
            <div className='row mb-0'>
              <div className='col-md-12'>
                <pre>{JSON.stringify(response, null, 2)}</pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default LinkContent
